import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'

interface WorkshopSectionProps {
  emoji: string
  title: string
  subtitle: string
  defaultOpen?: boolean
  /** When this flips to true, force the section open (used by the Add-block
   *  "reveal" so a scrolled-to card in a collapsed section is actually
   *  mounted). Non-destructive: it never auto-collapses, and only acts on the
   *  false→true transition, so the user can still collapse it afterward. */
  forceOpen?: boolean
  /** Stable DOM id for the jump-to-section handler (Task 14). Lives on this
   *  outer <section>, not the collapsible body, so it's a valid scroll
   *  target even while the section is collapsed. */
  id?: string
  children: ReactNode
}

/** Plain-language collapsible section shell (spec §4.3). */
export function WorkshopSection({ emoji, title, subtitle, defaultOpen = false, forceOpen = false, id, children }: WorkshopSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen])
  return (
    <section
      id={id}
      className="rounded-2xl border border-border bg-background shadow-xs transition-shadow duration-300 data-[flash=true]:ring-2 data-[flash=true]:ring-warning"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-3">
          <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10">{emoji}</span>
          <span>
            <span className="block font-semibold text-foreground">{title}</span>
            <span className="block text-xs text-muted-foreground">{subtitle}</span>
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-primary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t border-border px-4 py-4">{children}</div>}
    </section>
  )
}
